<script>
import { mapGetters, mapActions } from 'vuex';
import { useAlert } from 'dashboard/composables';

export default {
  props: {
    isEditingStaff: {
      type: Boolean,
      default: false,
    },
    branchId: {
      type: String,
      default: null,
    },
    editStaffId: {
      type: String,
      default: null,
    },
    resetCreateStaff: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      staffName: '',
      gender: '',
      seniority: '',
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters('appointments', ['selectedStaff']),
  },
  methods: {
    ...mapActions('appointments', ['createStaff', 'editStaff', 'fetchStaff', 'fetchStaffList']),

    async handleCreateStaff() {
      if (!this.staffName) {
        useAlert(this.$t('BRANCHES.CREATE_STAFF_MODAL.ALERT.FILL_FIELDS'));
        return;
      }

      if (this.isLoading) return;

      const payload = {
        staff_name: this.staffName,
        // gender and seniority intentionally omitted for now
      };

      this.isLoading = true;
      try {
        await this.createStaff({ branchId: this.branchId, payload });
      } catch (err) {
        console.error(err);
        useAlert(err);
      } finally {
        this.fetchStaffList(this.branchId);
        this.resetCreateStaff();
        this.isLoading = false;
      }
    },

    async handleEditStaff() {
      if (!this.staffName) {
        useAlert(this.$t('BRANCHES.CREATE_STAFF_MODAL.ALERT.FILL_FIELDS'));
        return;
      }

      if (this.isLoading) return;

      const payload = {
        staff_name: this.staffName,
        branch_id: this.branchId,
        // gender and seniority intentionally omitted for now
      };

      this.isLoading = true;
      try {
        await this.editStaff({ staffId: this.editStaffId, payload });
      } catch (err) {
        console.error(err);
        useAlert(err);
      } finally {
        this.fetchStaffList(this.branchId);
        this.resetCreateStaff();
        this.isLoading = false;
      }
    },
  },
  async mounted() {
    try {
      if (this.isEditingStaff) {
        await this.fetchStaff(this.editStaffId);
        this.staffName = this.selectedStaff.staff_name;
        this.gender = this.selectedStaff.gender || '';
        this.seniority = this.selectedStaff.seniority || '';
      }
    } catch (err) {
      console.error(err);
      useAlert(err);
    }
  },
};
</script>

<template>
  <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
    <!-- Staff Name -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {{ $t('BRANCHES.CREATE_STAFF_MODAL.LABEL.STAFF_NAME') }}
      </span>
      <input 
        type="text" 
        v-model="staffName" 
        :placeholder="$t('BRANCHES.CREATE_STAFF_MODAL.PLACEHOLDER.STAFF_NAME')"
        class="mt-1 w-full min-w-[250px] border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
      />
    </label>

    <!-- Gender -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
        Gender
      </span>
      <select
        v-model="gender"
        class="mt-1 w-full min-w-[250px] p-3 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
      >
        <option value="" disabled selected>-- Select Gender --</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
      </select>
    </label>

    <!-- Seniority -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
        Seniority
      </span>
      <input 
        type="text" 
        v-model="seniority" 
        placeholder="Please enter the seniority here"
        class="mt-1 w-full min-w-[250px] p-3 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
      />
    </label>

    <!-- Submit Button -->
    <button 
      @click="isEditingStaff ? handleEditStaff() : handleCreateStaff()"
      :disabled="isLoading"
      class="w-full py-2 mt-2 text-white font-medium rounded-md transition 
        bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-woot-600"
    >
      <span v-if="!isLoading">
        {{ isEditingStaff ? $t('BRANCHES.CREATE_STAFF_MODAL.BUTTON.EDIT') : $t('BRANCHES.CREATE_STAFF_MODAL.BUTTON.CREATE') }}
      </span>
      <span v-else class="flex items-center justify-center gap-2">
        <span class="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
        {{ isEditingStaff ? 'Editing...' : 'Creating...' }}
      </span>
    </button>
  </div>
</template>
